/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useContext, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";

// react-router-dom components
import { Link } from "react-router-dom";

// @mui material components
import Switch from "@mui/material/Switch";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

import AuthService from "services/auth-service";
import crudsService from "services/cruds-service";
import { AuthContext } from "context";

// Authentication layout components
import IllustrationLayout from "layouts/authentication/components/IllustrationLayout";

// Image
import bgImage from "assets/images/illustrations/illustration-reset.jpg";
import poweredByImage from "assets/images/quantuniversity-llc-logo.jpg";
import Footer from "examples/Footer";

function Login() {
  const navigate = useNavigate();
  const location = useLocation();

  const search = location.search || "";

  const params = new URLSearchParams(search);
  let redirectTo = params.get("redirect") || "/home-page";
  let accesscode = params.get("accesscode") || null;
  const authContext = useContext(AuthContext);
  const { getCurrentUser } = useContext(AuthContext);

  const [rememberMe, setRememberMe] = useState(false);
  const [inputs, setInputs] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState({
    emailError: false,
    passwordError: false,
    credentialErrors: false,
    textError: "",
  });

  const handleSetRememberMe = () => setRememberMe(!rememberMe);

  const changeHandler = (e) => {
    setInputs({
      ...inputs,
      [e.target.name]: e.target.value,
    });
  };

  const submitHandler = async (e) => {
    e.preventDefault();

    const mailFormat = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (
      inputs.email.trim().length === 0 ||
      !inputs.email.trim().match(mailFormat)
    ) {
      setErrors({ ...errors, emailError: true });
      return;
    }

    if (inputs.password.trim().length < 6) {
      setErrors({ ...errors, passwordError: true });
      return;
    }

    const newUser = { email: inputs.email, password: inputs.password };

    const myData = {
      data: {
        type: "token",
        attributes: { ...newUser },
      },
    };

    try {
      const response = await AuthService.login(myData);
      if (accesscode) {
        try {
          const userId = response.id;
          await crudsService.applyCouponCode({
            couponCode: accesscode,
            userId: userId,
          });

          console.log("Coupon code applied successfully");
        } catch (err) {
          console.error("Failed to apply coupon code:", err);
        }
      }
      if (!response.profile_completed && redirectTo === "/home-page") {
        redirectTo = "/home-page";
      }
      authContext.login(
        response.access_token,
        response.refresh_token,
        redirectTo,
      );
    } catch (res) {
      if (res.hasOwnProperty("message")) {
        setErrors({
          ...errors,
          credentialErrors: true,
          textError: res.message,
        });
      } else {
        setErrors({
          ...errors,
          credentialErrors: true,
          textError: res.errors[0].detail,
        });
      }
    }

    return () => {
      setInputs({
        email: "",
        password: "",
      });

      setErrors({
        emailError: false,
        passwordError: false,
        credentialErrors: false,
        textError: "",
      });
    };
  };

  return (
    <IllustrationLayout
      title="Sign In"
      illustration={bgImage}
      poweredBy={poweredByImage}
    >
      <MDBox
        component="form"
        role="form"
        method="POST"
        onSubmit={submitHandler}
      >
        <MDBox mb={2}>
          <MDInput
            type="email"
            label="Email"
            fullWidth
            name="email"
            value={inputs.email}
            onChange={changeHandler}
            error={errors.emailError}
          />
        </MDBox>
        <MDBox mb={2}>
          <MDInput
            type="password"
            label="Password"
            fullWidth
            name="password"
            value={inputs.password}
            onChange={changeHandler}
            error={errors.passwordError}
          />
        </MDBox>
        {errors.credentialErrors && (
          <MDTypography variant="caption" color="error" fontWeight="light">
            {errors.textError}
          </MDTypography>
        )}
        <MDBox
          mt={3}
          mb={1}
          style={{ display: "flex", justifyContent: "center" }}
        >
          <MDButton
            variant="gradient"
            color="info"
            style={{ width: "60%" }}
            type="submit"
          >
            sign in
          </MDButton>
        </MDBox>
        <MDBox mt={3} mb={1} textAlign="center">
          <MDTypography variant="button" color="text">
            <MDTypography
              component={Link}
              to="/auth/forgot-password"
              variant="button"
              color="info"
              fontWeight="medium"
              textGradient
            >
              Forgot your password?
            </MDTypography>
          </MDTypography>
        </MDBox>
        <MDBox mt={1} textAlign="center">
          <MDTypography variant="button" color="text">
            Don't have an account?{" "}
            <MDTypography
              component={Link}
              to={`/auth/register${search}`}
              variant="button"
              color="info"
              fontWeight="medium"
              textGradient
            >
              Sign up
            </MDTypography>
          </MDTypography>
        </MDBox>
      </MDBox>
    </IllustrationLayout>
  );
}

export default Login;
